import jwt_decode from "jwt-decode";
import useApi from "@/services/useApi";
import {
    COUNTRIES_SUPPORT_UKRAINE,
    TRANSLATE_LANGUAGES,
} from "@/data/countriesLanguageList.js";

const {api} = useApi();

// telephony and TikTok are open to these accounts only for now
const EARLY_ACCESS_EMAILS = [
    'telephony.test1@e-chat.tech',
    'telephony.test2@e-chat.tech',
    'telephony.test3@e-chat.tech',
];

const authModule = {
    namespaced: true,

    state: {
        token: localStorage.getItem("token") || "",
        language: localStorage.getItem("language") || "en",
        isUaLogo: "",
        userName: localStorage.getItem("user") || "",
        email: "",
        codeCountry: sessionStorage.getItem("code-country") || ""
    },

    getters: {
        getAuthHeader(state) {
            return {
                "Content-Type": "application/json",
                Authorization:
                    "Bearer " + state.token || localStorage.getItem("token"),
            };
        },
        isLoggedIn(state) {
            return !!state.token;
        },

        // dev builds show telephony and TikTok to everyone; the email is set on
        // sign-in, after a reload it comes from /api/me
        hasEarlyAccess(state, _getters, rootState) {
            if (process.env.NODE_ENV !== 'production') return true

            const email = state.email
                || rootState.user?.userData?.email
                || JSON.parse(localStorage.getItem('user-data'))?.email

            return EARLY_ACCESS_EMAILS.includes(email?.toLowerCase())
        },

        getToken(state) {
            return state.token;
        },

        getUserRole(state) {
            if (!state.token) return null;
            const decodedToken = jwt_decode(state.token);
            return decodedToken.role;
        },

        isAdmin(state, getters) {
            return getters.getUserRole === "admin";
        },

        getLanguage(state) {
            return state.language.toLowerCase();
        },

        getUserName(state) {
            return state.userName;
        },

        getCodeCountry(state) {
            return state.codeCountry;
        },
    },

    actions: {
        async REGISTER(_, payload) {
            const response = await api.post("/api/register", payload);
            return response;
        },

        async LOGIN({commit}, payload) {
            const response = await api.post("/api/login", payload);
            commit("SET_EMAIL", payload.email)
            commit("SET_TOKEN", response?.data?.data?.access_token);
            commit("SET_LANGUAGE", response?.data?.data?.language);
            commit("SET_USERNAME", response?.data?.data?.name);
            // read once by IntegrationPage: announcements follow a sign-in, not a reload
            sessionStorage.setItem("showAnnouncement", "1");
            return response;
        },

        // the next account must not start with this one's data: the messengers
        // page took its count of numbers and showed empty tables instead of
        // the start screen, the early access check read its email
        logout({commit}) {
            commit("CLEAR_TOKEN");
            commit("SET_EMAIL", "");
            commit("user/SET_USER_DATA", null, {root: true});
            localStorage.removeItem("user-data");
        },

        async FORGOT_PASSWORD(_, payload) {
            const response = await api.post("/api/forgot-password", payload);
            return response;
        },

        async RESET_PASSWORD(_, payload) {
            const response = await api.post("/api/reset-password", payload);

            return response;
        },

        // Смена языка в ЛК
        async updateLanguage({commit, getters, state}, newLanguage) {
            const currentLanguage = newLanguage || getters.getLanguage;
            try {
                await api.put(
                    "/api/language",
                    {
                        language: newLanguage || currentLanguage,
                    },
                    {
                        headers: {
                            Authorization: "Bearer " + state.token,
                        },
                    }
                );
                commit("SET_LANGUAGE", currentLanguage);
            } catch (error) {
                throw error;
            }
        },
        setTokenFromCookies({commit}) {
            const cookies = document.cookie.split("; ");
            const refreshTokenCookie = cookies.find((cookie) =>
                cookie.startsWith("refresh_profile_token=")
            );
            if (refreshTokenCookie) {
                const refreshToken = refreshTokenCookie.split("=")[1];
                commit("SET_TOKEN", refreshToken);
            } else {
                console.error("Refresh Token cookie not found");
            }
        },
        getLanguageFromCookie() {
            const cookies = document.cookie.split(";");
            let language = "";
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim().split("=");
                if (cookie[0] === "language") {
                    language = decodeURIComponent(cookie[1]);
                }
            }
            return language;
        },
        //Получение кода страны(запись в сессию+определяем логотип
        async getCodeCountry({commit}) {
            const response = await api.get("/api/code-country");
            const code = response?.data?.data?.code;
            commit("SET_CODE_COUNTRY", code);
            return code;
        },
    },

    mutations: {
        SET_EMAIL(state, email) {
            state.email = email
        },
        SET_TOKEN(state, token) {
            state.token = token;
            localStorage.setItem("token", token);
        },
        CLEAR_TOKEN(state) {
            console.debug("CLEAR_TOKEN");
            state.token = "";
            localStorage.removeItem("token");
            // a deep link kept for the next login must not survive a logout
            sessionStorage.removeItem("afterLoginPath");
            sessionStorage.removeItem("showAnnouncement");
        },

        SET_LANGUAGE(state, lang) {
            const defineLang =
                TRANSLATE_LANGUAGES.find((i) => i === lang.toLowerCase()) ||
                "en";
            const now = new Date();
            const time = now.getTime();
            const expireTime = time + 7 * 24 * 3600 * 1000;
            now.setTime(expireTime);
            document.cookie = `language=${defineLang.toUpperCase()}; path=/; expires=${now.toUTCString()}`;
            state.language = defineLang.toLowerCase();
            localStorage.setItem("language", state.language);
        },
        SET_IS_UKR_LOGO(state, codeCountry = "") {
            state.isUaLogo = COUNTRIES_SUPPORT_UKRAINE.includes(codeCountry);
        },
        SET_USERNAME(state, name) {
            if (!name) {
                state.userName = "";
                localStorage.setItem("user", "");
            } else {
                state.userName = name;
                localStorage.setItem("user", name);
            }
        },
        SET_CODE_COUNTRY(state, code) {
            state.codeCountry = code || "";
            sessionStorage.setItem("code-country", code || "");
        },
    },
};

export default authModule;
